// @ vendors
const { Promise } = require('es6-promise');
const moment = require('moment');
const { merge } = require('lodash');

// @ commons
const { APIGet, APIPost } = require('utilities/APIRequestHelper');
const apiURLBuilder = require('core/apiURLBuilder');

// @ utilities
const { isSafari } = require('../../insuranceUtilities/browserHelper');

// @ constants
const { FOURTH_STEP, DATE_FORMAT_API } = require('constants/index');
const { API_FLOW_STEPS, INVALID_DATE } = require('../../insuranceConstants/insuranceRequestHome');
const {
    INSURANCE_REQUEST_PRICING_REQUEST,
    INSURANCE_REQUEST_PRICING_SUCCESS,
    INSURANCE_REQUEST_PRICING_FAILURE,
    INSURANCE_REQUEST_PROVINCES_REQUEST,
    INSURANCE_REQUEST_PROVINCES_SUCCESS,
    INSURANCE_REQUEST_PROVINCES_FAILURE,
    INSURANCE_REQUEST_SET_PAYMENT_ACCOUNT_REQUEST,
    INSURANCE_REQUEST_SET_PAYMENT_ACCOUNT_SUCCESS,
    INSURANCE_REQUEST_SET_PAYMENT_ACCOUNT_FAILURE,
    INSURANCE_REQUEST_GET_PAYMENT_ACCOUNT_REQUEST,
    INSURANCE_REQUEST_GET_PAYMENT_ACCOUNT_SUCCESS,
    INSURANCE_REQUEST_GET_PAYMENT_ACCOUNT_FAILURE,
    INSURANCE_REQUEST_SAVE_STEP_REQUEST,
    INSURANCE_REQUEST_SAVE_STEP_SUCCESS,
    INSURANCE_REQUEST_SAVE_STEP_FAILURE,
    INSURANCE_REQUEST_GET_ADDRESS_TYPE_LIST_IN_PROGRESS,
    INSURANCE_REQUEST_GET_ADDRESS_TYPE_LIST_SUCCESS,
    INSURANCE_REQUEST_GET_ADDRESS_TYPE_LIST_FAILURE,
    INSURANCE_REQUEST_GET_MORTGAGE_BANK_LIST,
    INSURANCE_REQUEST_GET_MORTGAGE_BANK_LIST_SUCCESS,
    INSURANCE_REQUEST_GET_MORTGAGE_BANK_LIST_FAILURE,
} = require('../../insuranceConstants/actionTypes');

const requestInsurancePricingInProgress = () => ({
    type: INSURANCE_REQUEST_PRICING_REQUEST
});

const requestInsurancePricingSuccess = data => ({
    type: INSURANCE_REQUEST_PRICING_SUCCESS,
    data
});

const requestInsurancePricingFailure = error => ({
    type: INSURANCE_REQUEST_PRICING_FAILURE,
    data: { error }
});

const requestInsuranceProvincesInProgress = () => ({
    type: INSURANCE_REQUEST_PROVINCES_REQUEST
});

const requestInsuranceProvincesSuccess = data => ({
    type: INSURANCE_REQUEST_PROVINCES_SUCCESS,
    data
});

const requestInsuranceProvincesFailure = error => ({
    type: INSURANCE_REQUEST_PROVINCES_FAILURE,
    data: { error }
});

const setPaymentAccountRequest = () => ({
    type: INSURANCE_REQUEST_SET_PAYMENT_ACCOUNT_REQUEST
});

const setPaymentAccountSuccess = () => ({
    type: INSURANCE_REQUEST_SET_PAYMENT_ACCOUNT_SUCCESS
});

const setPaymentAccountFailure = error => ({
    type: INSURANCE_REQUEST_SET_PAYMENT_ACCOUNT_FAILURE,
    data: { error }
});

const getPaymentAccountRequest = () => ({
    type: INSURANCE_REQUEST_GET_PAYMENT_ACCOUNT_REQUEST
});

const getPaymentAccountSuccess = data => ({
    type: INSURANCE_REQUEST_GET_PAYMENT_ACCOUNT_SUCCESS,
    data
});

const getPaymentAccountFailure = error => ({
    type: INSURANCE_REQUEST_GET_PAYMENT_ACCOUNT_FAILURE,
    data: { error }
});

const saveStepRequest = step => ({
    type: INSURANCE_REQUEST_SAVE_STEP_REQUEST,
    data: { step }
});

const saveStepSuccess = (step, onlyUpdate) => ({
    type: INSURANCE_REQUEST_SAVE_STEP_SUCCESS,
    data: {
        step,
        onlyUpdate
    }
});

const saveStepFailure = (step, error) => ({
    type: INSURANCE_REQUEST_SAVE_STEP_FAILURE,
    data: { step, error }
});

const addressTypeListProgress = () => ({
    type: INSURANCE_REQUEST_GET_ADDRESS_TYPE_LIST_IN_PROGRESS
})

const addressTypeListSuccess = data => ({
    type: INSURANCE_REQUEST_GET_ADDRESS_TYPE_LIST_SUCCESS,
    data: { data }
});

const addressTypeListFailure = () => ({
    type: INSURANCE_REQUEST_GET_ADDRESS_TYPE_LIST_FAILURE
});

const getMortgageBankListRequest = () => ({
    type: INSURANCE_REQUEST_GET_MORTGAGE_BANK_LIST
});

const getMortgageBankListSuccess = bankList => ({
    type: INSURANCE_REQUEST_GET_MORTGAGE_BANK_LIST_SUCCESS,
    data: {
        bankList
    }
});

const getMortgageBankListSuccessFailure = error => ({
    type: INSURANCE_REQUEST_GET_MORTGAGE_BANK_LIST_FAILURE,
    data: { error }
});

const parseBanks = banks =>
    banks.map(bank => ({ id: bank.robotName, name: bank.frontName }))

const getMortgageBanks = () => (dispatch) => {
    dispatch(getMortgageBankListRequest())
    return APIGet(dispatch, apiURLBuilder.getURL('aggregatorGetBanks'))
        .then(response => dispatch(getMortgageBankListSuccess(parseBanks(response))))
        .catch(response => {
            if (!!response.error) {
                dispatch(getMortgageBankListSuccessFailure(response.error))
            } else {
                throw response;
            }
        });
};

const requestInsuranceCurrentPricing = insuranceId => (dispatch, getState) => {
    const immCurrentState = getState().insuranceRequestState;

    if (!immCurrentState.get('isFetchingPricing')) {
        dispatch(requestInsurancePricingInProgress());

        return APIGet({
            dispatch,
            url: apiURLBuilder.getURL('insuranceRequestPricingDiscount', { id: insuranceId }),
        })
            .then(response => {
                dispatch(requestInsurancePricingSuccess(response.data));
            })
            .catch(response => {
                if (!!response.error) {
                    dispatch(requestInsurancePricingFailure(response.error.description));
                } else {
                    throw response;
                }
            });
    } else {
        return Promise.resolve();
    }
};

const saveStep = (step, onlyUpdate) => (dispatch, getState) => {
    const currentState = getState().insuranceRequestState.get('data');
    const immStep = getState().insuranceRequestWizard.getIn(['steps', step]);

    let number = immStep.get('number');
    if (number === 's/n' || number === 'S/N') {
        number = null;
    }

    let uiStep = API_FLOW_STEPS.STEP_4;
    if (isSafari()) {
        uiStep = API_FLOW_STEPS.STEP_5;
    }
    const buildStartDate = moment(immStep.get('startDate')).format(DATE_FORMAT_API);

    const newState = {
        flowData: { uiStep },
        home: {
            additionalInfo: immStep.get('additionalInfo'),
            hasMortgage: immStep.get('hasMortgage'),
            mortgageBank: immStep.get('mortgageBank'),
            number,
            population: immStep.get('population'),
            roadType: !!immStep.get('roadType') ? immStep.get('roadType') : null,
            road: immStep.get('road'),
            provinceId: immStep.get('provinceId'),
        },
        payment: { paymentType: immStep.get('paymentType') },
        startDate: buildStartDate === INVALID_DATE ? null : buildStartDate
    };

    const requestData = {
        query: merge({}, currentState.toJS(), newState)
    };

    dispatch(saveStepRequest(step));

    return APIPost(
        dispatch,
        apiURLBuilder.getURL('insuranceRequestStateRequest', { id: currentState.get('id') }),
        requestData
    )
        .then(() => {
            dispatch(saveStepSuccess(step, onlyUpdate))
            if (onlyUpdate) {
                dispatch(requestInsuranceCurrentPricing(currentState.get('id')));
            }
        })
        .catch(response => {
            if (!!response.error) {
                dispatch(saveStepFailure(step, response.error));
            } else {
                throw response;
            }
        });
};

const formatProvincesResponse = (data) => {
    const provinces = data.reduce((provinces, nextProvices) => {
        provinces[nextProvices.id] = nextProvices;
        return provinces;
    }, {})
    return provinces;
}

const requestInsuranceProvinces = () => (dispatch) => {
    dispatch(requestInsuranceProvincesInProgress());
    return APIGet({
        dispatch,
        endpoint: 'getProvinces'
    })
        .then(response => {
            const provinces = formatProvincesResponse(response);
            dispatch(requestInsuranceProvincesSuccess(provinces));
        })
        .catch(response => {
            if (!!response.error) {
                dispatch(requestInsuranceProvincesFailure(response.error));
            } else {
                throw response;
            }
        })
}

const setPaymentAccount = () => (dispatch, getState) => {
    const currentState = getState().insuranceRequestState.get('data');
    const immStep = getState().insuranceRequestWizard.getIn(['steps', FOURTH_STEP]);

    const requestData = {
        query: {
            account: immStep.get('accountIBAN')
        }
    };

    dispatch(setPaymentAccountRequest());

    return APIPost(
        dispatch,
        apiURLBuilder.getURL('insuranceRequestPayment', { id: currentState.get('id') }),
        requestData
    )
        .then(() => {
            dispatch(setPaymentAccountSuccess());
            saveStep(FOURTH_STEP)(dispatch, getState);
        })
        .catch(response => {
            if (!!response.error) {
                dispatch(setPaymentAccountFailure(response.error));
            } else {
                throw response;
            }
        });
};

const getPaymentAccount = () => (dispatch, getState) => {
    const currentState = getState().insuranceRequestState;

    if (currentState.getIn(['data', 'id'])) {
        dispatch(getPaymentAccountRequest());

        return APIGet(
            dispatch,
            apiURLBuilder.getURL('insuranceRequestPayment', { id: currentState.getIn(['data', 'id']) })
        )
            .then(response => {
                dispatch(getPaymentAccountSuccess(response.data));
            })
            .catch(response => {
                if (!!response.error) {
                    dispatch(getPaymentAccountFailure(response.error));
                } else {
                    throw response;
                }
            });
    } else {
        return Promise.resolve();
    }
};

const getAddressTypeList = () => (dispatch) => {
    dispatch(addressTypeListProgress());
    return APIGet({
        dispatch,
        endpoint: 'insuranceRequestAddressListType'
    })
        .then(response => {
            const { data } = response;
            dispatch(addressTypeListSuccess(data));
        })
        .catch(() => {
            dispatch(addressTypeListFailure());
        });
};


module.exports = {
    requestInsuranceCurrentPricing,
    requestInsuranceProvinces,
    setPaymentAccount,
    getPaymentAccount,
    getAddressTypeList,
    getMortgageBanks,
    saveStep,
};
